import { DownloadIcon, LanguagesIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { type AnimesubCandidate, ApiError, fetchAnimesub, searchAnimesub } from "../lib/api";
import { stagger } from "../lib/motion";
import { cn } from "../lib/utils";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { Button } from "./ui/button";
import { LoadingNote } from "./ui/loading";
import { SkeletonRows } from "./ui/skeleton";

/**
 * Wybór polskich napisów z animesub.info dla odcinka, który właśnie leci.
 *
 * Lista jest **wyborem**, nie automatem, i to jest cały sens tego okna: do jednego
 * odcinka bywa kilka tłumaczeń zsynchronizowanych pod różne wydania, a od trafienia
 * w to właściwe zależy, czy napisy zejdą się z obrazem bez przesuwania. Dlatego
 * „Pasują do" jest tu widoczne przy każdej pozycji — to najważniejsze pole, jakie ten
 * serwis daje.
 *
 * Wpisy niepasujące do numeru odcinka **zostają na liście**, tylko niżej i bez
 * wyróżnienia: numer bywa zapisany tak, że parser go nie widzi, a ukrycie takiego
 * wpisu znaczyłoby „nie ma napisów" tam, gdzie są.
 */
export function SubtitlePicker({
  titleId,
  episodeNumber,
  mediaFileId,
  open,
  onOpenChange,
  onFetched,
}: {
  titleId: string;
  episodeNumber: number;
  mediaFileId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Wołane po pobraniu — odtwarzacz odświeża listę ścieżek i włącza nową. */
  onFetched: (trackId: string) => void;
}) {
  const [candidates, setCandidates] = useState<AnimesubCandidate[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setCandidates(null);
    setError(null);
    searchAnimesub(titleId, episodeNumber)
      .then((result) => {
        if (!cancelled) setCandidates(result.candidates);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setCandidates([]);
        setError(e instanceof ApiError ? e.message : "Nie udało się poszukać napisów");
      });
    return () => {
      cancelled = true;
    };
  }, [open, titleId, episodeNumber]);

  async function take(candidate: AnimesubCandidate): Promise<void> {
    setBusy(candidate.id);
    setError(null);
    try {
      const result = await fetchAnimesub({
        mediaFileId,
        titleId,
        episodeNumber,
        entryId: candidate.id,
      });
      onFetched(result.trackId);
      onOpenChange(false);
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.message : "Nie udało się pobrać napisów");
    } finally {
      setBusy(null);
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-2xl">
        <AlertDialogTitle className="flex items-center gap-2">
          <LanguagesIcon className="size-5 text-accent" />
          Polskie napisy z animesub.info
        </AlertDialogTitle>
        <AlertDialogDescription>
          {candidates === null
            ? `Odcinek ${episodeNumber}`
            : `Odcinek ${episodeNumber} — ${candidates.length} ${candidates.length === 1 ? "wpis" : "wpisów"}`}
        </AlertDialogDescription>

        {error !== null && <p className="text-sm text-destructive">{error}</p>}

        {candidates === null && (
          <div className="flex flex-col gap-3">
            <LoadingNote label="Szukam napisów w animesub.info…" />
            <SkeletonRows count={3} />
          </div>
        )}

        {candidates !== null && candidates.length === 0 && error === null && (
          <p className="text-sm text-muted-foreground">
            Serwis nie ma napisów do tego tytułu. Spróbuj po innym zapisie nazwy — wpisy bywają pod
            tytułem angielskim albo polskim.
          </p>
        )}

        {candidates !== null && candidates.length > 0 && (
          <ul className="flex max-h-80 flex-col gap-2 overflow-y-auto">
            {candidates.map((candidate, index) => (
              <li
                key={candidate.id}
                style={stagger(index)}
                className={cn(
                  "enter-rise flex flex-wrap items-center gap-3 rounded-lg border p-3 text-sm",
                  candidate.matchesEpisode ? "border-border" : "border-border/40 opacity-70",
                )}
              >
                <div className="flex min-w-0 flex-col gap-0.5">
                  <span className="truncate font-medium">{candidate.title}</span>
                  <span className="text-xs text-muted-foreground">
                    {candidate.author ?? "bez autora"}
                    {candidate.addedAt !== null && ` · ${candidate.addedAt}`}
                    {candidate.downloads !== null && ` · ${candidate.downloads} pobrań`}
                  </span>
                  {/*
                    Pole, po którym się wybiera: mówi, pod jakie wydanie napisy są
                    zsynchronizowane. Bez niego wybór między trzema tłumaczeniami
                    byłby losowaniem.
                  */}
                  {candidate.syncedTo !== null && (
                    <span className="text-xs text-accent">pasują do: {candidate.syncedTo}</span>
                  )}
                </div>
                <Button
                  size="sm"
                  variant={candidate.matchesEpisode ? "secondary" : "ghost"}
                  className="ml-auto"
                  disabled={busy !== null}
                  onClick={() => void take(candidate)}
                >
                  <DownloadIcon />
                  {busy === candidate.id ? "Pobieram…" : "Weź te"}
                </Button>
              </li>
            ))}
          </ul>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel>Zamknij</AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
